import { useAtom } from "jotai";
import { useState } from "react";

import { useImagesWarmFastCache } from "@design-lab/entities/artwork/model/useImagesFastCache";
import { useRefineAiImage } from "@design-lab/features/weave-design-lab/api/useRefineAiImage";
import { PromptForm } from "@design-lab/features/weave-design-lab/ui/components/PromptForm";
import { RefinementFormMobile } from "@design-lab/features/weave-design-lab/ui/components/RefinementFormMobile";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { trackImageEditToolError } from "@design-lab/shared/lib/analytics";
import { useRefineHistoryContext, aiRefineGeneratingAtom } from "@design-lab/entities/artwork";

type RefineWithAIToolProps = {
  isActive: boolean;
  artworkId: string;
  printableArea: { left: number; top: number; width: number; height: number };
};

export function RefineWithAITool({ isActive, artworkId, printableArea }: RefineWithAIToolProps) {
  const [prompt, setPrompt] = useState("");
  const [error, setError] = useState<string | undefined>();

  const { currentImageData, pushOperation, currentOperation } = useRefineHistoryContext();
  const refineMutation = useRefineAiImage();
  const { isDesktop } = useResponsive();
  const [isRefining, setIsRefining] = useAtom(aiRefineGeneratingAtom);
  const { warmCache } = useImagesWarmFastCache();

  if (!isActive) return null;

  const handleRefine = async (promptText: string) => {
    setError(undefined);

    if (!currentImageData || !currentOperation?.metadata) {
      throw new Error("No image available for refinement");
    }

    try {
      setIsRefining(true);
      const response = await fetch(currentImageData);
      const imageBlob = await response.blob();

      const result = await refineMutation.mutateAsync({
        refinementPrompt: promptText.trim(),
        image: imageBlob,
        dtfArtworkId: artworkId
      });

      const [cachedImage] = await warmCache([result.url]);

      if (!cachedImage) {
        throw new Error("Failed to load refined image");
      }

      const { base64: base64Data, image } = cachedImage;
      const imageWidth = image.naturalWidth;
      const imageHeight = image.naturalHeight;

      // Fit image dimensions to printable area
      const aspectRatio = imageHeight / imageWidth;
      const maxWidth = printableArea.width;
      const maxHeight = printableArea.height;
      const maxAllowedWidth = Math.min(maxWidth, maxHeight / aspectRatio);

      const newWidth = Math.min(maxAllowedWidth, imageWidth);
      const newHeight = newWidth * aspectRatio;

      // Center horizontally, align to top
      const x = printableArea.left + (printableArea.width - newWidth) / 2;
      const y = printableArea.top;

      pushOperation({
        type: "ai-refine",
        imageData: base64Data,
        metadata: {
          dimensions: { x, y, width: newWidth, height: newHeight }
        },
        timestamp: Date.now()
      });

      setPrompt("");
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Couldn't generate the image, please try again.";
      trackImageEditToolError("refine_with_ai", errorMessage, {
        error: err,
        artworkId,
        component: "RefineWithAITool"
      });
      setError(errorMessage);
    } finally {
      setIsRefining(false);
    }
  };

  return (
    <div
      className={`pointer-events-none absolute z-10 ${
        isDesktop
          ? "left-1/2 top-28 w-[92%] max-w-3xl -translate-x-1/2"
          : "left-1/2 top-28 w-[calc(100%-2rem)] max-w-lg -translate-x-1/2"
      }`}
    >
      <div
        className={`pointer-events-auto rounded-xl border border-gray-200 bg-white p-2 shadow-lg ${
          isDesktop ? "ml-auto w-full max-w-md" : ""
        }`}
      >
        {isDesktop ? (
          <PromptForm
            prompt={prompt}
            onPromptChange={setPrompt}
            onGenerate={handleRefine}
            isLoading={isRefining}
            error={error}
            isRefineMode
            showCredits
          />
        ) : (
          <RefinementFormMobile
            prompt={prompt}
            onPromptChange={setPrompt}
            onRefine={handleRefine}
            isLoading={isRefining}
            error={error}
          />
        )}
      </div>
    </div>
  );
}
